import type { LocationQueryValue } from 'vue-router';
import { isValidPostAuthRedirect } from '~/utils/authRedirect';

/**
 * Builds `/auth/{register|login}?redirect=<encoded-current-path>` URLs and
 * resolves a safe post-auth redirect target from the current `?redirect=` query.
 *
 * Used by:
 *  - Property page CTAs (sign-up / sign-in buttons, locked nav items, banners)
 *  - `pages/auth/register.vue` + `pages/auth/login.vue` after successful auth
 *
 * Open-redirect protection: the redirect target must start with `/` and must
 * NOT itself be an auth route. Public marketing landing pages (`/`, `/agents`,
 * `/masterclass/<variant>`…, see `utils/authRedirect.ts`) are never targets —
 * the link carries no `?redirect=` and old links fall back. Anything else
 * falls back to `/dashboard`.
 */

type AuthTarget = '/auth/register' | '/auth/login';

const DEFAULT_POST_AUTH_PATH = '/dashboard';

const firstQueryValue = (raw: LocationQueryValue | LocationQueryValue[] | undefined): string | null => {
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw ?? null;
};

export const useRedirectAfterAuth = () => {
  const route = useRoute();

  const buildRedirectUrl = (target: AuthTarget): string => {
    const current = route.fullPath;
    if (!isValidPostAuthRedirect(current)) return target;
    return `${target}?redirect=${encodeURIComponent(current)}`;
  };

  const resolveSafeRedirect = (fallback: string = DEFAULT_POST_AUTH_PATH): string => {
    const raw = firstQueryValue(route.query.redirect as LocationQueryValue | LocationQueryValue[] | undefined);
    if (!raw) return fallback;
    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      return fallback;
    }
    return isValidPostAuthRedirect(decoded) ? decoded : fallback;
  };

  return {
    buildRedirectUrl,
    resolveSafeRedirect,
  };
};
